import { createContext, useContext } from 'react';

import type { FeatureFlagInput } from '../enums/feature-flags';
import {
  evaluateFeatureFlag,
  isFeatureFlagConfig,
  validateFeatureFlag,
} from '../featureFlagConfig';
import type { FeatureFlagContext } from '../featureFlagConfig';
import useRemoteConfig from './useRemoteConfig';

export const FeatureFlagContextProvider = createContext<
  FeatureFlagContext | undefined
>(undefined);

type FeatureFlagProviderProps = {
  value: FeatureFlagContext;
  children: React.ReactNode;
};

export const FeatureFlagProvider = ({
  value,
  children,
}: FeatureFlagProviderProps) => (
  <FeatureFlagContextProvider.Provider value={value}>
    {children}
  </FeatureFlagContextProvider.Provider>
);

export const useFeatureFlagContext = (): FeatureFlagContext | undefined =>
  useContext(FeatureFlagContextProvider);

export function useFeatureFlag(
  flag: FeatureFlagInput,
  context: FeatureFlagContext = {}
): boolean {
  const config = useRemoteConfig(flag);
  const providedContext = useContext(FeatureFlagContextProvider);

  if (!isFeatureFlagConfig(config)) return false;

  // Validate in development
  if (process.env.NODE_ENV === 'development') {
    const errors = validateFeatureFlag(config);
    if (errors.length > 0) {
      console.error(`Feature flag validation errors for "${flag}":`, errors);
    }
  }

  const resolvedEnv =
    context.env ??
    providedContext?.env ??
    process.env.REACT_APP_DEPLOYMENT_ENV ??
    process.env.NODE_ENV ??
    undefined;

  return evaluateFeatureFlag(config, {
    ...providedContext,
    ...context,
    env: resolvedEnv,
  });
}
